import cn from 'classnames'
import { forwardRef, HTMLProps, useCallback, useState } from 'react'
import CurrencyInput, { CurrencyInputProps, formatValue } from 'react-currency-input-field'

import styles from './InputField.module.scss'

interface CommonInputFieldProps {
  name?: string
  startIcon?: JSX.Element
  endIcon?: JSX.Element
  descriptor?: string
  animated?: boolean
  error?: string
  onValueChange?: (value: number) => void
  outlined?: boolean // TODO
  inputClassName?: string
}

type InputFieldProps = CommonInputFieldProps &
  (
    | ({
        type?: 'currency' | 'number'
      } & CurrencyInputProps)
    | ({
        type?: string
      } & HTMLProps<HTMLInputElement>)
  )

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      startIcon,
      endIcon,
      placeholder,
      name,
      id,
      className,
      descriptor,
      animated = true,
      error = null,
      outlined,
      type,
      autoComplete = 'off',
      onValueChange,
      inputClassName = '',
      readOnly,
      disabled,
      style,
      ...props
    },
    inputRef
  ) => {
    const [point, setPoint] = useState(false)
    const [zero, setZero] = useState('')
    const commonProps = {
      name,
      id: id ?? name,
      disabled,
      readOnly,
      placeholder,
      'aria-invalid': !!error,
      'aria-describedby': `${name}-error`,
      autoComplete,
      className: cn(styles.input, inputClassName),
      title:
        typeof props.value === 'number'
          ? formatValue({ value: String(`${props.value}${point ? '.' : ''}${zero}`) })
          : props.value && String(props.value),
    }

    const handleValueChange = useCallback(
      (value: any) => {
        if (!Number.isNaN(value)) {
          const regexDecimal = /\.$|\.\d*0+$/
          const regexPoint = /\.$|\.0+$/
          const regexZero = /0+$/
          if (regexDecimal.test(value)) {
            setPoint(regexPoint.test(value))
            setZero(regexZero.test(value) ? value.match(regexZero)[0] : '')
            onValueChange?.(+value)
          } else {
            setPoint(false)
            setZero('')
            onValueChange?.(Number(value))
          }
        }
      },
      [onValueChange]
    )

    return (
      <div
        style={style}
        className={cn(
          styles.root,
          {
            [styles.animated]: animated,
            [styles.startIcon]: !!startIcon,
            [styles.endIcon]: !!endIcon,
            [styles.outlined]: outlined,
          },
          className
        )}
      >
        <div className={styles.wrapper}>
          {type === 'currency' || type === 'number' ? (
            <CurrencyInput
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              ref={inputRef}
              decimalsLimit={2}
              allowNegativeValue={false}
              decimalSeparator="."
              groupSeparator={type === 'currency' ? ',' : ' '}
              onValueChange={handleValueChange}
              readOnly={readOnly}
              value={String(`${props.value}${point ? '.' : ''}${zero}`)}
              {...commonProps}
              // {...props}
            />
          ) : (
            <input
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              ref={inputRef}
              type={type}
              {...commonProps}
              {...props}
            />
          )}
          <label htmlFor={id ?? name} className={styles.label} data-descriptor={descriptor}>
            {startIcon}
            <span className={styles.placeholder}>
              <span className={styles.text}>{placeholder}</span>
            </span>
            {endIcon}
          </label>
        </div>
        {error && (
          <div id={`${name}-error`} role="alert" className={styles.error}>
            {error}
          </div>
        )}
      </div>
    )
  }
)

export default InputField
